import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { siteData } from "@/lib/aht/pageData";
import { Logo } from "@/components/aht/Logo";
import { EASE, SPRING, scrollToId } from "@/lib/aht/hooks";

export function Nav({ active }) {
  const { ui, agent, proceeds } = siteData;
  const nav = ui.nav;
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  const links = nav.links.filter((l) => l.id !== "proceeds" || proceeds.show);

  const go = (id) => {
    setOpen(false);
    // Wait one frame so the sheet's scroll lock is released before scrolling.
    requestAnimationFrame(() => scrollToId(id, reduce));
  };

  useEffect(() => {
    const root = document.documentElement;
    if (open) {
      root.style.overflow = "hidden";
      root.setAttribute("data-overlay-open", "");
    } else {
      root.style.overflow = "";
      root.removeAttribute("data-overlay-open");
    }
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <header
        className="fixed inset-x-0 top-0 z-50 h-[52px] border-b border-hairline"
        style={{ background: "var(--nav-bg)", backdropFilter: "saturate(180%) blur(20px)", WebkitBackdropFilter: "saturate(180%) blur(20px)" }}
        data-testid="site-nav"
      >
        <div className="wrap flex h-full items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => go("top")}
            className="hit-44 -ml-1 flex h-full items-center rounded-chip px-1"
            aria-label={nav.homeLabel}
            data-testid="nav-logo-button"
          >
            <Logo height={42} />
          </button>

          <nav aria-label={nav.menuTitle} className="hidden lg:block">
            <ul className="flex items-center gap-1">
              {links.map((l) => {
                const isActive = active === l.id;
                return (
                  <li key={l.id}>
                    <button
                      type="button"
                      onClick={() => go(l.id)}
                      aria-current={isActive ? "true" : undefined}
                      data-testid={`nav-link-${l.id}`}
                      className={`relative flex h-11 items-center rounded-chip px-3 text-[14px] ${
                        isActive ? "font-medium text-ink" : "text-graphite hover:text-ink"
                      }`}
                    >
                      {l.label}
                      {isActive && (
                        <motion.span
                          layoutId="nav-active-bar"
                          transition={SPRING}
                          className="absolute bottom-[6px] left-1/2 h-[2px] w-4 -translate-x-1/2 rounded-full bg-accent"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="flex items-center gap-1">
            <a
              href={agent.calendly}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-sm hit-44"
              data-testid="nav-book-call-button"
            >
              {nav.bookCall}
            </a>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="-mr-2 flex h-11 w-11 items-center justify-center rounded-full lg:hidden"
              aria-label={nav.menuOpen}
              aria-expanded={open}
              aria-controls="mobile-menu"
              data-testid="nav-menu-button"
            >
              <Menu size={22} strokeWidth={1.75} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label={nav.menuTitle}
            className="fixed inset-0 z-[70] flex flex-col bg-paper"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduce ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0 }}
            transition={{ duration: 0.25, ease: EASE }}
            data-testid="mobile-menu-sheet"
          >
            <div className="wrap flex h-[52px] items-center justify-between border-b border-hairline">
              <Logo height={42} />
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="-mr-2 flex h-11 w-11 items-center justify-center rounded-full"
                aria-label={nav.menuClose}
                data-testid="mobile-menu-close-button"
                autoFocus
              >
                <X size={22} strokeWidth={1.75} aria-hidden="true" />
              </button>
            </div>
            <motion.nav
              className="wrap flex-1 overflow-y-auto py-6"
              initial={reduce ? false : { y: -8 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.3, ease: EASE }}
            >
              <ul>
                {links.map((l) => (
                  <li key={l.id} className="border-b border-hairline">
                    <button
                      type="button"
                      onClick={() => go(l.id)}
                      className={`flex min-h-[60px] w-full items-center text-left font-display text-[28px] font-semibold tracking-[-0.02em] ${
                        active === l.id ? "text-ink" : "text-graphite"
                      }`}
                      data-testid={`mobile-menu-link-${l.id}`}
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.nav>
            <div className="wrap pb-[max(24px,env(safe-area-inset-bottom))] pt-4">
              <a
                href={agent.calendly}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary w-full"
                data-testid="mobile-menu-book-call-button"
              >
                {nav.bookCall}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
